import { ClerkProvider } from '@clerk/nextjs';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { NoSsr } from '@mui/material';
import { GoogleTagManager } from '@next/third-parties/google';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { IntlProvider } from 'react-intl';

import { selectEmotionCache } from 'utils/emotion-cache.utils';
import { isLocal, isProd, isStaging } from 'utils/env.utils';

import { INTL_LOCALE_MAP } from 'constants/i18n.constants';
import { IS_LOYALTY_ENABLED } from 'constants/loyalty.constants';
import { useLocale } from 'hooks/useLocale';
import { useReportScamSite } from 'hooks/useReportScamSite';
import { useTrackAffiliateSource } from 'hooks/useTrackAffiliateSource';
import { AvoEnv, Environment, gtmDestination, initAvo } from 'services/avo';
import { AvoInspector } from 'services/avo/inspector';
import { messages } from 'services/intl';
import LazyMotion from 'services/motion';
import { createQueryClient } from 'services/react-query';
import { SnackbarProvider } from 'services/snackbars';
import { LoyaltyStoreProvider } from 'services/stores/loyalty/LoyaltyStoreProvider';
import { NofomoStoreProvider } from 'services/stores/StoreProvider';
import ThemeContext from 'services/theme-context';

import OnboardedCheck from 'components/@account/OnboardedCheck';
import LoyaltyCelebrationContainer from 'components/@loyalty/LoyaltyCelebrationContainer';
import Layout from 'components/@screens/Layout';
import DefaultSeo from 'components/@seo/DefaultSeo';

const ReactQueryDevtools = dynamic(() =>
  import(
    /* webpackChunkName: 'ReactQueryDevTools' */ '@tanstack/react-query-devtools'
  ).then((c) => c.ReactQueryDevtools),
);

const mapToAvoEnvironment = (() => {
  if (isProd) return Environment.PROD;
  if (isStaging) return Environment.STAGING;
  if (isLocal) return Environment.DEV;
  return Environment.DEV;
})();

// Initialize Avo outside React land
initAvo(
  {
    env: isProd ? AvoEnv.Prod : AvoEnv.Dev,
    inspector: AvoInspector,
    webDebugger: process.env.NEXT_PUBLIC_ENABLE_AVO_DEBUGGER === 'true',
    reportFailureAs: 'error',
    avoLogger: {
      logDebug: () => false,
      logError: () => false,
      logWarn: () => false,
    },
  },
  {
    platformType: 'web',
    hostName: 'nofomo.com',
    environment: mapToAvoEnvironment,
  },
  {},
  gtmDestination,
);

type PageProps = {
  dehydratedState: unknown;
};

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache;
  pageProps: PageProps;
}

const App = (props: MyAppProps) => {
  useTrackAffiliateSource();
  useReportScamSite();
  const { locale, defaultLocale } = useLocale();
  const { isPreview } = useRouter();
  const {
    emotionCache = selectEmotionCache(locale),
    Component,
    pageProps,
  } = props;

  const [queryClient] = useState(() =>
    createQueryClient({ preview: isPreview }),
  );

  const { dehydratedState, ...otherPageProps } = pageProps;

  return (
    <>
      <CacheProvider value={emotionCache}>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary state={dehydratedState}>
            <IntlProvider
              locale={INTL_LOCALE_MAP[locale]}
              defaultLocale={defaultLocale}
              messages={messages[locale]}
            >
              <ThemeContext locale={locale}>
                <NofomoStoreProvider>
                  <LoyaltyStoreProvider>
                    <LazyMotion>
                      <Layout>
                        <DefaultSeo />
                        <ClerkProvider
                          clerkJSVariant="headless"
                          telemetry={false}
                        >
                          <SnackbarProvider>
                            <Component {...otherPageProps} />
                            <NoSsr>
                              <OnboardedCheck />
                              {IS_LOYALTY_ENABLED && (
                                <LoyaltyCelebrationContainer />
                              )}
                            </NoSsr>
                          </SnackbarProvider>
                        </ClerkProvider>
                      </Layout>
                    </LazyMotion>
                  </LoyaltyStoreProvider>
                </NofomoStoreProvider>
              </ThemeContext>
            </IntlProvider>
          </HydrationBoundary>
          {isLocal && <ReactQueryDevtools buttonPosition="bottom-left" />}
        </QueryClientProvider>
      </CacheProvider>
      <GoogleTagManager
        gtmId={process.env.NEXT_PUBLIC_GTM_ID || ''}
        preview={process.env.NEXT_PUBLIC_GTM_PREVIEW}
        auth={process.env.NEXT_PUBLIC_GTM_AUTH}
      />
      <GoogleTagManager
        gtmId={process.env.NEXT_PUBLIC_GTM_GLOBAL_ID || ''}
        preview={process.env.NEXT_PUBLIC_GTM_GLOBAL_PREVIEW}
        auth={process.env.NEXT_PUBLIC_GTM_GLOBAL_AUTH}
      />
    </>
  );
};

export default App;
